import { useState } from 'react';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  useMemberNotes,
  useAddMemberNote,
  useDeleteMemberNote,
  type UserWithRole,
} from '@/hooks/useAdmin';
import { useToast } from '@/hooks/use-toast';
import {
  Mail,
  Linkedin,
  Building2,
  Briefcase,
  MapPin,
  User,
  Loader2,
  Plus,
  Trash2,
  FileText,
  Clock,
  ClipboardList,
} from 'lucide-react';

interface MemberDetailDrawerProps {
  member: UserWithRole | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    approved: {
      label: 'Active',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    },
    applied: {
      label: 'Applied',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    },
    rejected: {
      label: 'Rejected',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
  };

  const { label, className } = config[status] || { label: status, className: '' };

  return <Badge className={className}>{label}</Badge>;
}

function RoleBadge({ role }: { role: string }) {
  const config: Record<string, { label: string; className: string }> = {
    admin: {
      label: 'Admin',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
    moderator: {
      label: 'Moderator',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    },
    member: {
      label: 'Member',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    },
  };

  const { label, className } = config[role] || { label: role, className: '' };

  return <Badge variant="outline" className={className}>{label}</Badge>;
}

export default function MemberDetailDrawer({
  member,
  open,
  onOpenChange,
}: MemberDetailDrawerProps) {
  const [newNote, setNewNote] = useState('');
  const { toast } = useToast();

  const { data: notes, isLoading: notesLoading } = useMemberNotes(member?.id || null);
  const addNote = useAddMemberNote();
  const survey = member?.survey || null;
  const deleteNote = useDeleteMemberNote();

  const handleAddNote = async () => {
    if (!member || !newNote.trim()) return;

    try {
      await addNote.mutateAsync({
        profileId: member.id,
        content: newNote.trim(),
      });
      setNewNote('');
      toast({ title: 'Note added' });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add note';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!member) return;

    try {
      await deleteNote.mutateAsync({
        noteId,
        profileId: member.id,
      });
      toast({ title: 'Note deleted' });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete note';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  if (!member) return null;

  const userRole = member.user_roles?.role || 'member';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <SignedAvatarImage src={member.avatar_url || undefined} />
              <AvatarFallback className="bg-primary/10 text-primary text-xl">
                {member.full_name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-xl truncate">{member.full_name}</SheetTitle>
              {member.headline && (
                <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                  {member.headline}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <StatusBadge status={member.membership_status} />
                <RoleBadge role={userRole} />
                {member.is_mentor && (
                  <Badge variant="secondary">Mentor</Badge>
                )}
              </div>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-6">
            {/* Contact Info */}
            <section>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Contact</h3>
              <div className="space-y-2">
                {member.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <a
                      href={`mailto:${member.email}`}
                      className="text-primary hover:underline truncate"
                    >
                      {member.email}
                    </a>
                  </div>
                )}
                {member.linkedin_url && (
                  <div className="flex items-center gap-2 text-sm">
                    <Linkedin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <a
                      href={member.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline truncate"
                    >
                      {member.linkedin_url.replace('https://www.linkedin.com/in/', '')}
                    </a>
                  </div>
                )}
              </div>
            </section>

            <Separator />

            {/* Profile Details */}
            <section>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Profile</h3>
              <div className="space-y-3">
                {(member.company || member.role) && (
                  <div className="flex items-start gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      {member.role && <span>{member.role}</span>}
                      {member.role && member.company && <span> at </span>}
                      {member.company && <span className="font-medium">{member.company}</span>}
                    </div>
                  </div>
                )}
                {member.industry && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span>{member.industry}</span>
                  </div>
                )}
                {member.city_cohort && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span>{member.city_cohort}</span>
                  </div>
                )}
                {member.pronouns && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span>{member.pronouns}</span>
                  </div>
                )}
              </div>
              {member.bio && (
                <p className="text-sm mt-3 text-muted-foreground whitespace-pre-wrap">
                  {member.bio}
                </p>
              )}
            </section>

            <Separator />

            {/* Survey Response */}
            {survey && (
              <>
                <section>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Survey Response
                  </h3>
                  <div className="space-y-4 bg-muted/50 rounded-lg p-4">
                    {survey.submitted_at && (
                      <p className="text-xs text-muted-foreground">
                        Submitted {format(new Date(survey.submitted_at), 'MMM d, yyyy')}
                      </p>
                    )}
                    {survey.about && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          About Themselves
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{survey.about}</p>
                      </div>
                    )}
                    {survey.goals && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          What They Want From The Network
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{survey.goals}</p>
                      </div>
                    )}
                    {survey.engagement_style && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          How They Want To Engage
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{survey.engagement_style}</p>
                      </div>
                    )}
                  </div>
                </section>

                <Separator />
              </>
            )}

            {/* Onboarding Answers */}
            {member.onboarding_answers && member.onboarding_answers.length > 0 && (
              <>
                <section>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    Onboarding Answers
                  </h3>
                  <div className="space-y-4 bg-muted/50 rounded-lg p-4">
                    {member.onboarding_answers.map((answer, idx) => (
                      <div key={idx}>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          {answer.question}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{answer.response}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <Separator />
              </>
            )}

            {/* Admin Notes */}
            <section>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Admin Notes
              </h3>

              {/* Add note input */}
              <div className="space-y-2 mb-4">
                <Textarea
                  placeholder="Add a note about this member..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="min-h-[80px] text-sm"
                />
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  disabled={!newNote.trim() || addNote.isPending}
                >
                  {addNote.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Note
                </Button>
              </div>

              {/* Notes timeline */}
              {notesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : notes && notes.length > 0 ? (
                <div className="space-y-3">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-muted/50 rounded-lg p-3 group relative"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">
                          {note.admin_name || 'Admin'}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteNote(note.id)}
                            disabled={deleteNote.isPending}
                          >
                            <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No notes yet. Add one above.
                </p>
              )}
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
